import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";
import { usePopoverPosition } from "@/components/use-popover-position";

export type RowAction = {
  label: string;
  icon: string;
  onClick: () => void;
  tone?: "default" | "danger";
  disabled?: boolean;
  /** Tooltip explaining why the action is disabled. Ignored if not disabled. */
  disabledReason?: string;
};

/**
 * Generic per-row overflow (⋮) menu. Portal-rendered so it escapes any
 * `overflow-auto` ancestor (a table scroller, a card) that would otherwise
 * clip an absolutely-positioned popover, and right-aligned to the trigger.
 * Clicks/keys are stopped so they don't bubble to a clickable row underneath.
 */
export function RowActionsMenu({ actions, label }: { actions: RowAction[]; label: string }) {
  const [open, setOpen] = useState(false);
  const [triggerEl, setTriggerEl] = useState<HTMLButtonElement | null>(null);
  const [popoverEl, setPopoverEl] = useState<HTMLDivElement | null>(null);
  const pos = usePopoverPosition({ anchorEl: open ? triggerEl : null, popoverEl, align: "right" });

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const inTrigger = triggerEl?.contains(e.target as Node);
      const inPopover = popoverEl?.contains(e.target as Node);
      if (!inTrigger && !inPopover) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, triggerEl, popoverEl]);

  return (
    <>
      <button
        ref={setTriggerEl}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          // Don't let Enter/Space bubble to the row's select handler.
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
        className={[
          "flex size-8 items-center justify-center rounded-[4px] transition-colors",
          open ? "bg-grey-100 text-black" : "text-grey-600 hover:bg-grey-100 hover:text-black",
        ].join(" ")}
      >
        <Icon name="more_vert" size={20} />
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={setPopoverEl}
            role="menu"
            aria-label={label}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? "visible" : "hidden",
            }}
            /* z-[1400]: above the MUI Dialog (zIndex.modal=1300). */
            className="fixed z-[1400] flex min-w-[208px] flex-col rounded-[4px] border border-grey-200 bg-white p-1 shadow-soft"
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                disabled={action.disabled}
                aria-disabled={action.disabled || undefined}
                title={action.disabled ? action.disabledReason : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (action.disabled) return;
                  setOpen(false);
                  action.onClick();
                }}
                className={[
                  "flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-small font-medium transition-colors",
                  action.disabled
                    ? "cursor-not-allowed text-grey-400"
                    : [
                        "hover:bg-grey-50",
                        action.tone === "danger" ? "text-red-600" : "text-black",
                      ].join(" "),
                ].join(" ")}
              >
                <Icon
                  name={action.icon}
                  size={20}
                  className={
                    action.disabled
                      ? "shrink-0 text-grey-300"
                      : action.tone === "danger"
                        ? "shrink-0 text-red-600"
                        : "shrink-0 text-grey-900"
                  }
                />
                <span>{action.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
